import { useEffect } from 'react'

interface UseCatalogKeyboardShortcutsProps {
  onSave: () => void
  onDismissSelection?: () => void
  hasLoaded: boolean
  hasUnsavedChanges: boolean
  hasSelection?: boolean
}

const shouldConsumeEscapeLocally = (element: HTMLElement) => {
  if (element.isContentEditable) return true

  const tagName = element.tagName
  if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') {
    return true
  }

  const role = element.getAttribute('role')
  if (role === 'combobox' || role === 'textbox' || role === 'listbox') {
    return true
  }

  return Boolean(
    element.closest(
      '[role="combobox"], [role="textbox"], [role="listbox"], [contenteditable="true"]',
    ),
  )
}

export const useCatalogKeyboardShortcuts = ({
  onSave,
  onDismissSelection,
  hasLoaded,
  hasUnsavedChanges,
  hasSelection = false,
}: UseCatalogKeyboardShortcutsProps) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Save shortcut: Ctrl+S or Cmd+S
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        if (hasLoaded && hasUnsavedChanges) {
          onSave()
        }
      }

      if (e.key === 'Escape' && hasSelection && onDismissSelection) {
        if (e.defaultPrevented) return

        const activeElement = document.activeElement
        if (
          activeElement instanceof HTMLElement &&
          activeElement !== document.body
        ) {
          if (shouldConsumeEscapeLocally(activeElement)) {
            activeElement.blur()
            return
          }

          activeElement.blur()
        }

        e.preventDefault()
        onDismissSelection()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [hasLoaded, hasSelection, hasUnsavedChanges, onDismissSelection, onSave])
}
